import { Box, IconButton, Stack } from '@mui/material';
import TextField from '@mui/material/TextField';
import { AsYouType } from 'libphonenumber-js';
import * as React from 'react';
import { useUpdateContactField } from '../../../sic-lib/api/hooks/useContact';
import { FieldValidationType } from '../../../sic-lib/enums/FieldValidationType';
import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import { Contact } from '../../../sic-lib/types/Contact';
import {
    buildUpdateContactField,
    ContactAddress,
    ContactField,
    ContactFieldValue,
} from '../../../sic-lib/types/ContactField';
import { parseContactFieldError } from '../../../sic-lib/utils/contactFieldHelpers';
import EditButtonGroup from '../../Shared/FormControls/Buttons/EditButtonGroup';
import TextFieldFastAddress from '../../Shared/TextFieldFastAddress/TextFieldFastAddress';
import ContactFieldIcon from './ContactFieldIcon';

export default function ContactFieldEdit({ contact, contactField, onToggleHandler }: Props) {
    const { mutate: updateContactField, error } = useUpdateContactField(contact.contactId);

    const [formState, formDispatch] = React.useReducer<GenericReducer<EditorState>>(
        genericReducer,
        {
            fieldValue:
                contactField.validationType === FieldValidationType.usMobilePhone
                    ? new AsYouType('US').input(contactField.value.toString())
                    : contactField.value,
        },
    );

    const inputType = React.useMemo(() => {
        switch (contactField.validationType) {
            case FieldValidationType.date:
                return 'date';
            case FieldValidationType.years:
            case FieldValidationType.dollars:
                return 'number';
            default:
                return 'text';
        }
    }, [contactField.validationType]);

    const handleSetFieldValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        switch (contactField.validationType) {
            case FieldValidationType.usMobilePhone:
                if (event.target.value.includes('(') && !event.target.value.includes(')')) {
                    //fix a case where hitting backspace on a (416 number, reformats back to (416)
                    formDispatch({
                        fieldValue: event.target.value.replace('(', ''),
                    });
                } else {
                    formDispatch({
                        fieldValue: new AsYouType('US').input(event.target.value),
                    });
                }
                break;
            default:
                formDispatch({ fieldValue: event.target.value });
                break;
        }
    };

    const handleSave = async () => {
        const updateField = buildUpdateContactField({
            ...contactField,
            ...{
                fieldName: 'value',
                value: formState.fieldValue,
            },
        });

        updateContactField(updateField, {
            onSuccess: () => {
                onToggleHandler();
            },
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') handleSave();
    };

    return (
        <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Box>
                <IconButton aria-label={contactField.validationType.toString()} disabled>
                    <ContactFieldIcon validationType={contactField.validationType} />
                </IconButton>
            </Box>
            <Stack spacing={1} width="100%">
                {contactField.validationType === FieldValidationType.address ? (
                    <Stack spacing={2}>
                        <TextFieldFastAddress
                            addressFields={formState.fieldValue as ContactAddress}
                            addressLabel={contactField.label}
                            onChangeHandler={formDispatch}
                            error={!!error}
                            groupHelperText={parseContactFieldError(error)}
                            size="small"
                            fullWidth
                        />
                    </Stack>
                ) : (
                    <TextField
                        id={`fld_${contactField.fieldId}`}
                        name={contactField.label}
                        label={contactField.label}
                        value={formState.fieldValue}
                        type={inputType}
                        onChange={handleSetFieldValue}
                        onKeyDown={handleKeyDown}
                        error={!!error}
                        helperText={parseContactFieldError(error)}
                        fullWidth
                        size="small"
                    />
                )}
                <EditButtonGroup
                    onSave={handleSave}
                    onCancel={onToggleHandler}
                    allowDelete={false}
                    size="small"
                />
            </Stack>
        </Stack>
    );
}

interface EditorState {
    fieldValue: ContactFieldValue;
}

interface Props {
    contact: Contact;
    contactField: ContactField;
    onToggleHandler: () => void;
}
